import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";

/**
 * Podpowiedź danych podmiotu po numerze NIP (rejestr REGON, usługa BIR).
 *
 * Odpytujemy **po odejściu z pola albo po dziesiątej cyfrze**, nigdy przy
 * każdym naciśnięciu klawisza: limit wywołań GUS-u jest wspólny dla całej
 * instalacji, a numer niepełny i tak nie ma sensu.
 *
 * Wynik jest podpowiedzią, nie rozstrzygnięciem — nazwa wraca na ekran do
 * potwierdzenia wzrokowego i nie nadpisuje tego, co ktoś już wpisał.
 */

export interface RegistryCompany {
  nip: string;
  regon: string;
  name: string;
  kind: string;
  address_line: string;
  postal_code: string;
  city: string;
  terminated_on: string | null;
}

export interface NipLookup {
  status: "ok" | "not_found" | "unavailable";
  source: string;
  fetched_at: string | null;
  warnings: string[];
  company: RegistryCompany | null;
}

export function normalizeNip(value: string): string {
  return value.replace(/[^0-9]/g, "").slice(0, 10);
}

export interface NipLookupState {
  wynik: NipLookup | null;
  trwa: boolean;
  /** Komunikat pod pole — wyłącznie o numerze, nie o rejestrze. */
  bladPola: string;
}

export function useNipLookup(nip: string): NipLookupState {
  const numer = normalizeNip(nip);
  const [stan, setStan] = useState<NipLookupState>({
    wynik: null, trwa: false, bladPola: "",
  });

  useEffect(() => {
    if (numer.length !== 10) {
      setStan({ wynik: null, trwa: false, bladPola: "" });
      return;
    }

    let aktualne = true;
    setStan((poprzedni) => ({ ...poprzedni, trwa: true }));

    api
      .get<NipLookup>(`/registry/nip/${numer}`)
      .then((wynik) => {
        if (aktualne) setStan({ wynik, trwa: false, bladPola: "" });
      })
      .catch((e) => {
        if (!aktualne) return;
        // 400 to niepoprawna suma kontrolna — jedyny przypadek, w którym
        // rejestr mówi coś o samym numerze, a nie o podmiocie.
        const bladPola =
          e instanceof ApiError ? (e.field("nip") ?? "") : "";
        setStan({ wynik: null, trwa: false, bladPola });
      });

    return () => {
      aktualne = false;
    };
  }, [numer]);

  return stan;
}

/** Jedno zdanie pod polem — to, co człowiek ma przeczytać i porównać. */
export function komunikatRejestru(stan: NipLookupState): string {
  if (stan.bladPola) return stan.bladPola;
  if (stan.trwa) return "Sprawdzam w rejestrze REGON…";
  const wynik = stan.wynik;
  if (!wynik) return "";
  if (wynik.status === "not_found") {
    return "Rejestr REGON nie zna tego numeru — sprawdź, czy nie ma literówki.";
  }
  if (wynik.status === "unavailable") {
    return "Rejestr REGON jest chwilowo niedostępny. Nazwę wpisz ręcznie.";
  }
  return wynik.company ? `REGON: ${wynik.company.name}` : "";
}
